import { getPdtProjectsV2, PDT_VIEW_TYPES } from 'common/services/pdt.service';
import { METHOD_NAMES } from 'modals/CreateEditProjectModal/consts';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { myAccountSelector } from 'state/selectors/account';
import { PDT_EMPTY_STATE_TYPES, PDT_PAGE_SIZE } from '../projects.consts';
import { useLocation } from 'react-router';
import AppRouter from 'common/AppRouter';
import { useDebounceCallback } from '@react-hook/debounce';

const useFetchPdtProjctsData = ({ selectedPeriod, timeRange }) => {
  const account = useSelector(myAccountSelector);
  const favoriteProjectsIds = account?.settings?.favorite_projects || [];
  const location = useLocation();

  const { pathname } = location;

  const isFavoriteProjectsView = useMemo(() => {
    return pathname === AppRouter.PROJECTS_API_FAVORITES.path;
  }, [pathname]);

  const [projects, setProjects] = useState([]);
  const [isDataFetchedByProjectId, setisDataFetchedByProjectId] = useState({});
  const [totalProjects, setTotalProjects] = useState(0);
  const [isLightDataFetched, setIsLightDataFetched] = useState(false);
  const [isLoadingLightData, setIsLoadingLightData] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PDT_PAGE_SIZE);
  const [allProjectsTotal, setAllProjectsTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [emptyStateType, setEmptyStateType] = useState(
    PDT_EMPTY_STATE_TYPES.NO_DATA
  );
  const fetchAllProjectsTotal = async () => {
    const data = await fetchData({ view: PDT_VIEW_TYPES.LIGHT });
    setAllProjectsTotal(data?.[0]?.total_projects);
  };

  const onInitState = () => {
    setIsLoadingLightData(true);
    setIsLightDataFetched(false);
    setisDataFetchedByProjectId({});
  };

  const checkEmptyState = () => {
    if (!projects.length && !keyword.length) {
      setEmptyStateType(PDT_EMPTY_STATE_TYPES.NO_DATA);
    } else if (!projects.length && keyword.length) {
      setEmptyStateType(PDT_EMPTY_STATE_TYPES.NO_SEARCH_RESULTS);
    } else {
      setEmptyStateType(null);
    }
  };

  const fetchData = async ({
    cadence = null,
    view = PDT_VIEW_TYPES.DEFAULT,
    projectIds = [],
    page = 1
  }) => {
    return await getPdtProjectsV2({
      selectedPeriod,
      timeRange,
      page,
      limit: pageSize,
      view,
      projectIds,
      keyword
    });
  };

  const fetchLightData = async ({ projectIds = [] } = {}) => {
    try {
      onInitState();
      const data = await fetchData({
        view: PDT_VIEW_TYPES.LIGHT,
        page,
        projectIds
      });
      setProjects(data);
      setTotalProjects(data?.[0]?.total_projects);
      setIsLightDataFetched(true);
    } catch (error) {
      setProjects([]);
    } finally {
      setIsLoadingLightData(false);
    }
  };

  const fetchKanban = async ({ projectIds }) => {
    projectIds?.map(async projectId => {
      try {
        const data = await fetchData({
          cadence: METHOD_NAMES.KANBAN.type,
          projectIds: [projectId]
        });
        setProjects(prevProjects =>
          prevProjects.map(
            project => data?.find(item => item.id === project.id) || project
          )
        );
      } catch (error) {
      } finally {
        setisDataFetchedByProjectId(prevState => ({
          ...prevState,
          [projectId]: true
        }));
      }
    });
  };

  const fetchScrum = async ({ projectIds }) => {
    try {
      const data = await fetchData({
        cadence: METHOD_NAMES.SCRUM.type,
        projectIds
      });
      setProjects(prevProjects =>
        prevProjects.map(
          project => data?.find(item => item.id === project.id) || project
        )
      );
    } catch (error) {
    } finally {
      const projectIdsLoadinState = projectIds.reduce((acc, projectId) => {
        return { ...acc, [projectId]: true };
      }, {});
      setisDataFetchedByProjectId(prevState => ({
        ...prevState,
        ...projectIdsLoadinState
      }));
    }
  };

  const fetchDataByCadence = async ({ cadence } = {}) => {
    const isKanban = cadence === METHOD_NAMES.KANBAN.type;
    const projectIds = projects
      .filter(item => item.cadence.type === cadence)
      .map(({ id }) => id);
    if (isKanban) {
      await fetchKanban({ projectIds });
    } else {
      await fetchScrum({ projectIds });
    }
  };

  const fetchProjects = useDebounceCallback(async () => {
    if (isFavoriteProjectsView) {
      fetchLightData({ projectIds: favoriteProjectsIds });
    } else {
      await fetchLightData();
    }
  }, 200);

  useEffect(() => {
    const fetchAllData = async () => {
      await fetchDataByCadence({ cadence: METHOD_NAMES.SCRUM.type });
      await fetchDataByCadence({ cadence: METHOD_NAMES.KANBAN.type });
    };
    if (isLightDataFetched) {
      fetchAllData();
    }
  }, [isLightDataFetched]);

  const fetchDebounce = useDebounceCallback(async () => {
    fetchProjects();
  }, 500);

  useEffect(() => {
    fetchDebounce();
  }, [
    selectedPeriod,
    timeRange,
    page,
    pageSize,
    isFavoriteProjectsView,
    keyword
  ]);

  useEffect(() => {
    setPage(1);
  }, [isFavoriteProjectsView, keyword]);

  useEffect(() => {
    fetchAllProjectsTotal();
  }, []);

  useEffect(() => {
    checkEmptyState();
  }, [projects]);

  return {
    projects,
    setProjects,
    isDataFetchedByProjectId,
    totalProjects,
    isLoadingLightData,
    allProjectsTotal,
    paginationHandler: { page, setPage, pageSize, setPageSize },
    keyword,
    setKeyword,
    emptyStateType
  };
};

export default useFetchPdtProjctsData;
